/**
 * A single-line text that marquees on hover of its entry: truncated with an ellipsis at rest,
 * on hover it slides left just far enough to show its end, and snaps back on leave.
 * A text that fits does nothing. Distance and duration come from a measurement (ResizeObserver),
 * so the speed is constant whatever the overflow; `motion-reduce` shows the end without the glide.
 *
 * The hover is the row's (`group/row`), not the text's own — the row is the target the
 * pointer is on. With `focusTrigger`, keyboard focus on the row (`:focus-visible`) runs the
 * same glide — for rows that are themselves the focusable control (start-screen suggestions).
 *
 * Shared by the conversation list (`ChatBot`) and the start-screen suggestions
 * (`Suggestions`, variant `list`).
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type Ref } from 'react';

import { cn } from '@/lib/utils';

/** Pixels per second of glide. */
export const MARQUEE_SPEED = 40;
/** Pause before the glide starts, so a pass over the list does not set every row moving. */
export const MARQUEE_DELAY_MS = 350;

export interface MarqueeTextProps {
    text: string;
    className?: string;
    title?: string;
    testId?: string;
    /** No glide at all — a plain truncated line (the selected conversation, for one). */
    disabled?: boolean;
    /** Also glide while the `group/row` element has keyboard focus (`:focus-visible`). */
    focusTrigger?: boolean;
    /**
     * `span` for phrasing-only contexts such as inside a `<button>` (a `div` is not allowed
     * there); it is rendered as a block so the ellipsis and the measurement still work.
     */
    as?: 'div' | 'span';
}

// Full class strings, so Tailwind's scanner sees every variant.
const HOVER_OUTER = 'group-hover/row:[text-overflow:clip]';
const FOCUS_OUTER = 'group-focus-visible/row:[text-overflow:clip]';
const HOVER_INNER =
    'group-hover/row:inline-block group-hover/row:transition-transform group-hover/row:ease-linear group-hover/row:[transform:translateX(var(--marquee-shift))] motion-reduce:group-hover/row:transition-none';
const FOCUS_INNER =
    'group-focus-visible/row:inline-block group-focus-visible/row:transition-transform group-focus-visible/row:ease-linear group-focus-visible/row:[transform:translateX(var(--marquee-shift))] motion-reduce:group-focus-visible/row:transition-none';

export function marqueeStyle(overflow: number): CSSProperties {
    if (overflow <= 0) return {};
    return {
        '--marquee-shift': `${-overflow}px`,
        transitionDuration: `${Math.max(0.3, overflow / MARQUEE_SPEED)}s`,
        transitionDelay: `${MARQUEE_DELAY_MS}ms`,
    } as CSSProperties;
}

export function MarqueeText({
    text,
    className,
    title,
    testId,
    disabled = false,
    focusTrigger = false,
    as: Outer = 'div',
}: MarqueeTextProps) {
    const outerRef = useRef<HTMLElement | null>(null);
    const innerRef = useRef<HTMLSpanElement | null>(null);
    const [measured, setMeasured] = useState(0);
    const overflow = disabled ? 0 : measured;

    useLayoutEffect(() => {
        const outer = outerRef.current;
        const inner = innerRef.current;
        if (!outer || !inner) return;
        // Measured on the outer block: an `inline` span reports scrollWidth 0.
        const measure = () => setMeasured(Math.max(0, outer.scrollWidth - outer.clientWidth));
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(outer);
        return () => observer.disconnect();
    }, [text]);

    return (
        <Outer
            ref={outerRef as Ref<HTMLDivElement & HTMLSpanElement>}
            className={cn(
                // `truncate` at rest: the ellipsis only renders for inline text, so the inner
                // span is `inline` until the hover turns it into an `inline-block` for the
                // transform — and the ellipsis gives way to a plain clip at the same moment.
                'truncate',
                Outer === 'span' && 'block',
                overflow > 0 && HOVER_OUTER,
                overflow > 0 && focusTrigger && FOCUS_OUTER,
                className,
            )}
            title={title}
            data-testid={testId}
            data-marquee={overflow > 0 ? 'true' : 'false'}
        >
            <span
                ref={innerRef}
                className={cn(
                    // The glide exists only while hovered: leaving the row snaps the text back at
                    // once (user decision 28.09.2026), no return animation.
                    'inline transition-none',
                    overflow > 0 && HOVER_INNER,
                    overflow > 0 && focusTrigger && FOCUS_INNER,
                )}
                style={marqueeStyle(overflow)}
            >
                {text}
            </span>
        </Outer>
    );
}
